import { Box, Button, Link, Separator, Spinner, VStack } from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"

import {
    NonArchivedDiscussionResponseFragment,
    useDiscussionContext
} from "../DiscussionProvider"
import DiscussionEmpty from "./DiscussionEmpty"
import DiscussionResponseForm from "./DiscussionResponseForm"
import DiscussionSingleResponse from "./DiscussionSingleResponse"
import ResponseListItem from "./ResponseListItem"
import ResponseListItemArchived from "./ResponseListItemArchived"
import useDiscussionResponsePermissions from "./useDiscussionResponsePermissions"

function ResponseList() {
  const { t } = useTranslate()
  const cohortId = useDiscussionContext((c) => c.post?.cohort_id!)
  const responses = useDiscussionContext((c) => c.responses)
  const responsesLoading = useDiscussionContext((c) => c.responsesLoading)
  const editingResponse = useDiscussionContext((c) => c.editingResponse)
  const responsesPaginator = useDiscussionContext((c) => c.responsesPaginator)
  const permissions = useDiscussionResponsePermissions(cohortId)

  // Render empty state
  if (responses.length === 0) {
    // Loading state while empty, show a spinner.
    if (responsesLoading) {
      return (
        <VStack>
          <Spinner size="md" />
        </VStack>
      )
    } else {
      // Empty state message
      const emptyMessage = permissions.canCreate
        ? t("discussion.responses.empty.can_respond")
        : t("discussion.responses.empty.cant_respond")

      return <DiscussionEmpty message={emptyMessage} />
    }
  }

  return (
    <Box className="response-list-view">
      <VStack gap={0} align="stretch">
        {/* Response items */}
        <VStack
          className="response-list-item-container"
          gap={4}
          align="stretch"
          separator={<Separator borderBottomWidth={0} borderTopWidth="1px" />}
        >
          <DiscussionSingleResponse />
          {responses.map((response, index) => (
            <Box key={response.id || index}>
              {response.archived_time ? (
                <ResponseListItemArchived
                  response={response}
                  allResponses={responses}
                />
              ) : response.id === editingResponse?.id ? (
                <DiscussionResponseForm
                  response={response as NonArchivedDiscussionResponseFragment}
                />
              ) : (
                <ResponseListItem
                  response={response as NonArchivedDiscussionResponseFragment}
                />
              )}
            </Box>
          ))}
        </VStack>

        {/* Load more pagination */}
        {responsesPaginator.hasMore && (
          <Link asChild variant="underline">
            <Button
              variant="plain"
              size="sm"
              mt={4}
              onClick={responsesPaginator.loadMore}
              disabled={responsesPaginator.loadingMore}
              className="paginate next"
            >
              {responsesPaginator.loadingMore ? (
                <>
                  <Spinner size="sm" mr={2} />
                  {t("discussion.response_list.loading")}...
                </>
              ) : (
                t("discussion.response_list.load_more", {
                  next: responsesPaginator.nextPageCount
                })
              )}
            </Button>
          </Link>
        )}
      </VStack>
    </Box>
  )
}

export default ResponseList
